import { useMemo } from "react";
import { Link } from "react-router-dom";
import type { RouterOutput } from "../lib/trpc.js";
import { Card, Empty, ResultBadge, StatTile, StatusBadge, TeamTag } from "./ui.js";
import type { RecordOf } from "./GameRow.js";
import { formatDateTime, gameSlot, recordLabel } from "../lib/format.js";

type ScheduleData = RouterOutput["league"]["schedule"];
type ScheduleGame = ScheduleData["weeks"][number]["games"][number];

/** Someone who appears in a competition, as read off its own schedule. */
export interface Participant {
  managerId: number;
  name: string;
  slug: string | null;
}

type GameFixture = { kind: "game"; key: string; slot: string; game: ScheduleGame };

/**
 * One line of a manager's season: a game they play, or a week they sit out.
 *
 * A week where they do neither is still listed. A schedule with a hole in it is
 * a fact about the season worth seeing, and skipping the week would make the
 * list read as if the season were shorter than it is.
 */
type Fixture = GameFixture | { kind: "off"; key: string; slot: string; recorded: boolean };

/**
 * Everyone who takes part in a competition, in alphabetical order.
 *
 * Read from the schedule the page has already fetched rather than from the
 * participants endpoint: a manager who plays a game or takes a bye is in the
 * competition by definition, and this costs no second request.
 */
export function scheduleParticipants(data: ScheduleData): Participant[] {
  const found = new Map<number, Participant>();
  const add = (managerId: number | null, name: string | null, slug: string | null) => {
    if (managerId == null || !name) return;
    if (!found.has(managerId)) found.set(managerId, { managerId, name, slug });
  };

  for (const w of data.weeks) {
    for (const g of w.games) {
      add(g.home.managerId, g.home.managerName, g.home.managerSlug);
      add(g.away.managerId, g.away.managerName, g.away.managerSlug);
    }
    for (const b of w.byes) add(b.managerId, b.name, b.slug);
  }
  // A tournament can be all bracket and no weeks, and a manager knocked out in
  // the first round of one would otherwise be missing from the picker.
  for (const g of data.bracket) {
    add(g.home.managerId, g.home.managerName, g.home.managerSlug);
    add(g.away.managerId, g.away.managerName, g.away.managerSlug);
  }

  return [...found.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Everything one manager plays, in the order they play it.
 *
 * The bracket is kept apart from the weeks. It isn't part of "week by week",
 * and folding its matches into the same tally would have a manager's record
 * here disagree with the same manager's row in the standings table one tab
 * over, which counts the regular season.
 */
function fixturesFor(data: ScheduleData, managerId: number) {
  const playing = (g: ScheduleGame) =>
    g.home.managerId === managerId || g.away.managerId === managerId;
  const asFixture = (g: ScheduleGame): GameFixture => ({
    kind: "game",
    key: `g${g.id}`,
    slot: gameSlot(g),
    game: g,
  });

  const weeks: Fixture[] = [];
  for (const w of data.weeks) {
    const games = w.games.filter(playing);
    if (games.length > 0) {
      weeks.push(...games.map(asFixture));
    } else {
      weeks.push({
        kind: "off",
        key: `w${w.id}`,
        slot: w.label ?? `Week ${w.number}`,
        recorded: w.byes.some((b) => b.managerId === managerId),
      });
    }
  }

  return { weeks, playoffs: data.bracket.filter(playing).map(asFixture) };
}

/** The two sides of a game, from one manager's point of view. */
function sidesFor(game: ScheduleGame, managerId: number) {
  const isHome = game.home.managerId === managerId;
  const own = isHome ? game.home : game.away;
  const opponent = isHome ? game.away : game.home;
  const result =
    game.status !== "final"
      ? null
      : game.winnerManagerId === managerId
        ? "W"
        : game.winnerManagerId == null
          ? "T"
          : "L";
  return { isHome, own, opponent, result: result as "W" | "L" | "T" | null };
}

function FixtureRow({
  fixture,
  managerId,
  records,
}: {
  fixture: Fixture;
  managerId: number;
  records?: RecordOf;
}) {
  if (fixture.kind === "off") {
    return (
      <div className="flex items-center gap-3 border-b border-field-800 px-4 py-3 last:border-b-0">
        <div className="w-20 shrink-0 text-xs text-chalk-500">{fixture.slot}</div>
        <div className="text-sm text-chalk-500">
          {fixture.recorded ? "Bye" : "No game scheduled"}
        </div>
      </div>
    );
  }

  const { game } = fixture;
  const { isHome, own, opponent, result } = sidesFor(game, managerId);
  const isFinal = game.status === "final";
  const found = opponent.managerId != null ? records?.get(opponent.managerId) : undefined;
  // Before a season kicks off every record is 0-0, which is ten rows of noise
  // rather than ten pieces of information.
  const record = found && found.wins + found.losses + found.ties > 0 ? found : null;

  return (
    <Link
      to={`/games/${game.id}`}
      className="flex items-center gap-3 border-b border-field-800 px-4 py-3 last:border-b-0 hover:bg-field-850/60"
    >
      <div className="w-20 shrink-0 text-xs text-chalk-500">
        {fixture.slot}
        {game.matchupType && (
          <div className="mt-0.5 text-[11px] text-chalk-500">{game.matchupType}</div>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="truncate font-semibold text-chalk-50">
          {/* The row is a link to the game, so the opponent stays plain text —
              an <a> inside an <a> is invalid and browsers split the outer one. */}
          <span className="mr-1.5 text-chalk-500">{isHome ? "vs" : "at"}</span>
          {opponent.managerName ?? opponent.placeholder ?? "TBD"}
          {record && (
            <span
              className="ml-2 text-xs font-normal text-chalk-500"
              title="Their record in this competition"
            >
              {recordLabel(record.wins, record.losses, record.ties)}
            </span>
          )}
        </div>
        {/* Nothing is picked until the week opens, so this whole line is
            absent for a season that hasn't started. */}
        {(opponent.teamAbbr || own.teamAbbr) && (
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-chalk-500">
            <TeamTag abbr={opponent.teamAbbr} />
            {own.teamAbbr && (
              <>
                <span>·</span>
                <span>you:</span>
                <TeamTag abbr={own.teamAbbr} />
              </>
            )}
          </div>
        )}
      </div>

      <div className="shrink-0 text-right">
        {isFinal ? (
          <div className="flex items-center justify-end gap-2">
            <span className="font-mono text-base font-bold tabular-nums">
              {own.score}
              <span className="mx-1 text-chalk-600">–</span>
              {opponent.score}
            </span>
            <ResultBadge result={result} />
          </div>
        ) : (
          <>
            <StatusBadge status={game.status} />
            {game.scheduledFor && (
              <div className="mt-1 text-[11px] text-chalk-500">
                {formatDateTime(game.scheduledFor)}
              </div>
            )}
          </>
        )}
      </div>
    </Link>
  );
}

/**
 * One manager's season, week by week: who they face, where, and how it went.
 *
 * The season page already holds the whole schedule, so this is a filter over
 * data that is on the page rather than another query.
 */
export default function ManagerSchedule({
  data,
  manager,
  records,
}: {
  data: ScheduleData;
  manager: Participant;
  records?: RecordOf;
}) {
  const { weeks, playoffs } = useMemo(
    () => fixturesFor(data, manager.managerId),
    [data, manager.managerId],
  );

  const isGame = (f: Fixture): f is GameFixture => f.kind === "game";
  const games = weeks.filter(isGame);
  const played = games.filter((f) => f.game.status === "final");

  const tallyOf = (list: GameFixture[]) =>
    list.reduce(
      (acc, f) => {
        const { result } = sidesFor(f.game, manager.managerId);
        if (result === "W") acc.wins++;
        else if (result === "L") acc.losses++;
        else if (result === "T") acc.ties++;
        return acc;
      },
      { wins: 0, losses: 0, ties: 0 },
    );

  const tally = tallyOf(played);
  const playoffTally = tallyOf(playoffs.filter((f) => f.game.status === "final"));

  // What's next is the first unplayed game anywhere in the competition — a
  // manager still in the bracket is waiting on a bracket game, not a week.
  const next = [...games, ...playoffs].find((f) => f.game.status !== "final");
  const nextOpponent = next ? sidesFor(next.game, manager.managerId) : null;
  const byes = weeks.filter((f) => f.kind === "off" && f.recorded);
  const isTournament = data.season.kind !== "season";

  const row = (f: Fixture) => (
    <FixtureRow key={f.key} fixture={f} managerId={manager.managerId} records={records} />
  );

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile
          label="Record"
          value={recordLabel(tally.wins, tally.losses, tally.ties)}
          hint={`${played.length} of ${games.length} played`}
        />
        <StatTile
          label="Next up"
          value={
            nextOpponent
              ? `${nextOpponent.isHome ? "vs" : "at"} ${
                  nextOpponent.opponent.managerName ??
                  nextOpponent.opponent.placeholder ??
                  "TBD"
                }`
              : "—"
          }
          hint={next ? next.slot : "Nothing left to play"}
        />
        <StatTile
          label="Home / away"
          value={`${games.filter((f) => sidesFor(f.game, manager.managerId).isHome).length} / ${
            games.filter((f) => !sidesFor(f.game, manager.managerId).isHome).length
          }`}
        />
        <StatTile
          label="Bye"
          value={byes.length > 0 ? byes.map((b) => b.slot).join(", ") : "—"}
          hint={byes.length > 0 ? undefined : "No bye"}
        />
      </div>

      <Card
        title={`${manager.name}'s ${isTournament ? "run" : "season"}`}
        action={
          manager.slug ? (
            <Link
              to={`/managers/${manager.slug}`}
              className="text-sm text-chalk-400 hover:text-blitz-400"
            >
              Profile →
            </Link>
          ) : null
        }
      >
        {weeks.length === 0 ? (
          <Empty>{manager.name} has no games in this competition.</Empty>
        ) : (
          weeks.map(row)
        )}
      </Card>

      {playoffs.length > 0 && (
        <Card
          title="Playoffs"
          action={
            <span className="text-xs text-chalk-500">
              {recordLabel(playoffTally.wins, playoffTally.losses, playoffTally.ties)} in the
              bracket
            </span>
          }
        >
          {playoffs.map(row)}
        </Card>
      )}
    </div>
  );
}
